/**
 * useListStagger — first-mount entrance stagger for list/table rows.
 *
 * Design intent (WS-1 "List entrance"): the first time a table body or feed
 * paints, rows fade+rise in sequence (30ms/row, capped at 8) so the eye tracks
 * the list filling in. On refetch / re-render the rows must NOT re-animate — a
 * queue that re-staggers on every poll reads as flicker, not polish.
 *
 * Implementation: a mounted-ref latches on the first render where the list is
 * ready (has real rows — see the `ready` param), so an empty/loading first
 * render doesn't consume the one-shot. `getStaggerProps` returns
 * `{ className, style }` for a row at `index`:
 *   - first mount  → `stagger-item` class + `--stagger-index` (index, capped) so
 *     the shared keyframe (motion-utilities.css) delays by index * 30ms.
 *   - after mount  → empty props (no animation).
 * The delay is capped at STAGGER_CAP so long lists don't wait seconds for the
 * tail; rows beyond the cap animate with the cap's delay (no visible pause).
 *
 * PRM is handled entirely in CSS (motion-utilities.css collapses the keyframe to
 * a 100ms no-delay fade under reduced-motion), so this hook stays motion-agnostic.
 */
import { useRef, type CSSProperties } from "react";

/** Max staggered rows — beyond this the delay plateaus (brief: cap at 8). */
export const STAGGER_CAP = 8;

/** Per-row stagger step in ms — mirrored by the calc() in motion-utilities.css. */
export const STAGGER_STEP_MS = 30;

export interface StaggerRowProps {
  className: string;
  style: CSSProperties;
}

export interface UseListStaggerResult {
  /** Props to spread on the row element at `index`. Empty after first mount. */
  getStaggerProps: (index: number) => StaggerRowProps;
}

/**
 * @param enabled - pass false to opt out entirely (returns empty props always).
 * @param ready - defaults to true. Pass the "have real rows arrived?" signal
 *   (e.g. `rows.length > 0` or `!isLoading`) so the one-shot stagger latches on
 *   the first render that actually paints rows, NOT on an empty/loading render.
 *   Without this, a consumer that first renders empty consumes the single
 *   first-mount stagger and the real rows never animate. Defaulting to true
 *   keeps existing call sites (which pass no second arg) behaving as before.
 */
export function useListStagger(enabled = true, ready = true): UseListStaggerResult {
  // Latches true on the FIRST render where the list is ready (rows present).
  // Because a ref mutation during render is read-only-until-next-render for
  // children, we capture the first-ready decision once and hold it stable for
  // the life of the list. An empty/loading first render (ready=false) does not
  // latch, so the stagger is preserved for when real rows arrive.
  const hasMountedRef = useRef(false);
  const isFirstMount = enabled && ready && !hasMountedRef.current;
  if (isFirstMount) {
    hasMountedRef.current = true;
  }

  const getStaggerProps = (index: number): StaggerRowProps => {
    if (!enabled || !isFirstMount) {
      return { className: "", style: {} };
    }
    const capped = Math.min(index, STAGGER_CAP);
    return {
      className: "stagger-item",
      style: { ["--stagger-index" as string]: capped },
    };
  };

  return { getStaggerProps };
}
